import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

type PresenceSize = 'xs' | 'sm' | 'md';

type UserPresenceAvatarWrapProps = {
  children: ReactNode;
  /** When false, renders children only (no dot). */
  showPresence: boolean;
  isOnline: boolean;
  /** Tooltip when presence is shown */
  onlineLabel?: string;
  offlineLabel?: string;
  /**
   * Dot scale vs avatar: `xs` ≈18px avatars (forms/chips), `sm` compact lists, `md` default.
   */
  size?: PresenceSize;
  /** Sit dot on the outer rim; use when parent allows overflow: visible (chips, header). */
  dotOutset?: boolean;
  sx?: any;
};


function dotDiameter(size: PresenceSize, dotOutset: boolean): number {
  if (size === 'xs') return dotOutset ? 7 : 6;
  if (size === 'sm') return dotOutset ? 9 : 8;
  return 10;
}

function borderWidthPx(size: PresenceSize, dotOutset: boolean): number {
  if (size === 'xs') return 1.5;
  if (size === 'sm') return dotOutset ? 2 : 1.5;
  return 2;
}

/**
 * Wraps an avatar with a small circular online/offline dot (tenant agent socket presence).
 */
export function UserPresenceAvatarWrap({
  children,
  showPresence,
  isOnline,
  onlineLabel = 'Active in app',
  offlineLabel = 'Away',
  size = 'md',
  dotOutset = false,
  sx,
}: UserPresenceAvatarWrapProps) {

  const dotPx = dotDiameter(size, dotOutset);
  const inset = size === 'sm' || size === 'xs' ? 0 : -1;

  const dotPosition = dotOutset
    ? size === 'xs'
      ? {
          right: 0,
          bottom: 0,
          zIndex: 2,
          transform: 'translate(6%, 6%)',
        }
      : size === 'sm'
        ? {
            right: 0,
            bottom: 0,
            zIndex: 2,
            transform: 'translate(8%, 8%)',
          }
        : {
            right: 0,
            bottom: 0,
            zIndex: 2,
            transform: 'translate(32%, 32%)',
          }
    : {
        right: size === 'xs' ? '1px' : size === 'sm' ? '2px' : `${inset}px`,
        bottom: size === 'xs' ? '1px' : size === 'sm' ? '2px' : `${inset}px`,
        zIndex: 1,
      };

  const bw = borderWidthPx(size, dotOutset);

  const dot = (
    <Box
      component="span"
      sx={{
        position: 'absolute',
        ...dotPosition,
        width: dotPx,
        height: dotPx,
        minWidth: dotPx,
        minHeight: dotPx,
        maxWidth: dotPx,
        maxHeight: dotPx,
        flexShrink: 0,
        borderRadius: '50%',
        bgcolor: isOnline ? 'success.main' : 'action.disabledBackground',
        border: (theme) => `${bw}px solid ${theme.palette.background.paper}`,
        boxSizing: 'border-box',
        boxShadow:
          size === 'md'
            ? (theme) =>
                theme.palette.mode === 'dark'
                  ? `0 0 0 1px ${theme.palette.common.black}`
                  : `0 0 0 1px ${theme.palette.grey[200]}`
            : (theme) =>
                `0 0 0 0.5px ${theme.palette.mode === 'dark' ? theme.palette.common.black : theme.palette.grey[400]}`,
      }}
    />
  );

  const content = (

    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
        verticalAlign: 'middle',
        ...sx,
      }}
    >
      {children}
      {showPresence && dot}
    </Box>
  );

  if (!showPresence) {
    return content;
  }

  return (
    <Tooltip title={isOnline ? onlineLabel : offlineLabel} placement="top" arrow>
      {content}
    </Tooltip>
  );
}

